import axios from "axios"
import Cookies from "js-cookie"

export const SIGNUP_STATES = {
  idle: "idle",
  submitting: "submitting",
  success: "success",
  error: "error",
  cooldown: "cooldown",
}

export const SIGNUP_MIN_AGE_MS = 3 * 1000
export const SIGNUP_MAX_AGE_MS = 30 * 60 * 1000
export const SIGNUP_COOLDOWN_MS = 10 * 1000

const SIGNUP_ENDPOINT = "/.netlify/functions/signUpHandler"
const SIGNUP_COOLDOWN_KEY = "ig-signup-cooldown-until"
const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i

const isBrowser = typeof window !== "undefined"

const getStorage = () => {
  if (!isBrowser) return null

  try {
    return window.localStorage
  } catch (error) {
    return null
  }
}

const clearCooldownStorage = () => {
  const storage = getStorage()

  if (storage) {
    storage.removeItem(SIGNUP_COOLDOWN_KEY)
  }

  if (isBrowser) {
    Cookies.remove(SIGNUP_COOLDOWN_KEY)
  }
}

const readCooldownUntil = () => {
  const storage = getStorage()
  const storedValue =
    (storage && storage.getItem(SIGNUP_COOLDOWN_KEY)) ||
    (isBrowser ? Cookies.get(SIGNUP_COOLDOWN_KEY) : null)

  const cooldownUntil = Number(storedValue)

  if (!Number.isFinite(cooldownUntil)) {
    clearCooldownStorage()
    return 0
  }

  return cooldownUntil
}

export const createSignupStartedAt = () => Date.now()

export const shouldRefreshStartedAt = startedAt =>
  !Number.isFinite(startedAt) || Date.now() - startedAt > SIGNUP_MAX_AGE_MS

export const validateSignupValues = values => {
  const errors = {}
  const email = typeof values.email === "string" ? values.email.trim() : ""

  if (!email || !EMAIL_REGEX.test(email)) {
    errors.email = "Please enter a valid email address"
  }

  return errors
}

export const buildSignupPayload = ({ email, source, website, startedAt }) => ({
  email: email.trim().toLowerCase(),
  source,
  website: website || "",
  startedAt,
})

export const getSignupCooldownRemainingMs = () => {
  const cooldownUntil = readCooldownUntil()

  if (!cooldownUntil) return 0

  const remainingMs = cooldownUntil - Date.now()

  if (remainingMs <= 0) {
    clearCooldownStorage()
    return 0
  }

  // Clear stale cooldowns left over from older, longer-duration values.
  if (remainingMs > SIGNUP_COOLDOWN_MS) {
    clearCooldownStorage()
    return 0
  }

  return remainingMs
}

export const setSignupCooldown = (durationMs = SIGNUP_COOLDOWN_MS) => {
  const cooldownUntil = Date.now() + durationMs
  const storage = getStorage()

  if (storage) {
    storage.setItem(SIGNUP_COOLDOWN_KEY, String(cooldownUntil))
  }

  if (isBrowser) {
    Cookies.set(SIGNUP_COOLDOWN_KEY, String(cooldownUntil), {
      expires: durationMs / (24 * 60 * 60 * 1000),
    })
  }

  return cooldownUntil
}

export const formatCooldownMessage = remainingMs => {
  if (remainingMs < 60 * 1000) {
    const secondsRemaining = Math.max(1, Math.ceil(remainingMs / 1000))

    return `Please wait ${secondsRemaining} second${
      secondsRemaining === 1 ? "" : "s"
    } before trying again.`
  }

  const minutesRemaining = Math.max(1, Math.ceil(remainingMs / (60 * 1000)))

  return `Please wait ${minutesRemaining} minute${
    minutesRemaining === 1 ? "" : "s"
  } before trying again.`
}

export const getSignupErrorMessage = error => {
  if (error && error.statusCode === 429) {
    return (
      error.message ||
      "Too many signup attempts. Please wait a few seconds before trying again."
    )
  }

  return (
    (error && error.message) ||
    "We couldn't process your signup right now. Please try again."
  )
}

export const submitSignupRequest = async payload => {
  try {
    const response = await axios.post(SIGNUP_ENDPOINT, payload, {
      headers: {
        "Content-Type": "application/json",
      },
    })

    return response.data
  } catch (error) {
    const responseData = error.response && error.response.data
    const normalizedError = new Error(
      (responseData && responseData.message) ||
        "We couldn't process your signup right now. Please try again."
    )

    normalizedError.statusCode =
      (error.response && error.response.status) || 500
    normalizedError.reason = responseData && responseData.reason

    throw normalizedError
  }
}
